import type {
  ChangeEvent,
  CSSProperties,
  KeyboardEventHandler,
  ReactElement,
} from 'react';
import { twMerge } from 'tailwind-merge';
import InputAdornment from '@mui/material/InputAdornment';
import {
  default as MuiTextField,
  type TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField';
import { CloseIcon } from '@/icons';
import { classes, styles } from './utils';

export interface UnderlineTextFieldProps extends Omit<
  Partial<MuiTextFieldProps<'standard'>>,
  'onChange'
> {
  filled?: boolean;
  showCloseIcon?: boolean;
  className?: string;
  style?: CSSProperties;
  inputClassName?: string;
  inputStyle?: MuiTextFieldProps['sx'];
  placeholder?: string;
  value: string;
  endAdornment?: ReactElement;
  onChange: (value: string) => void;
}

export default function UnderlineTextField({
  filled,
  showCloseIcon,
  className,
  inputClassName,
  style = {},
  inputStyle = {},
  placeholder = 'Enter value...',
  value,
  onChange,
  endAdornment,
  ...props
}: UnderlineTextFieldProps) {
  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(e.target.value);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (!['Escape', 'Enter'].includes(e.key)) {
      e.stopPropagation();
    }
  };

  const renderEndAdornment = (): ReactElement | undefined => {
    if (value.length > 0 && showCloseIcon) {
      return (
        <InputAdornment position="end">
          <CloseIcon
            className={classes.closeIcon}
            onClick={() => onChange('')}
          />
        </InputAdornment>
      );
    }
    return endAdornment;
  };

  return (
    <div style={style} className={twMerge(classes.wrapper, className)}>
      <MuiTextField
        sx={{ ...styles.input, ...inputStyle }}
        className={inputClassName}
        autoComplete="off"
        color={filled ? 'grey-8' : 'orange-2'}
        variant={filled ? 'filled' : 'standard'}
        placeholder={placeholder}
        value={value}
        InputProps={{ endAdornment: renderEndAdornment() }}
        onKeyDown={handleKeyDown}
        onChange={handleSearch}
        {...props}
      />
    </div>
  );
}
